import { moduleDataTypePrefix, moduleDataTypes, moduleWidgetTypePrefix } from '../../shared/constants/input-types.constants';
import { InputOutputType } from '../enums/input-output-type.enum';
import { ModuleDataType, UiControlType } from '../types';
import {
    CheckboxWidget,
    DropdownWidget,
    FileDialogWidget,
    FileListSpecificationModel,
    FileSpecificationModel,
    IntegerSpecificationModel,
    ModulePropertyConstructor,
    ModulePropertySpecification,
    ModuleWidget,
    ModuleWidgetConstructor,
    NumberSpecificationModel,
    RadioButtonGroupWidget,
    RangeSliderWidget,
    RangeSpecificationModel,
    SliderWidget,
    StringResource,
    StringSpecificationModel,
    TextBoxWidget,
} from './module-control-specification.model';
import { ModulePropertyOptionItem } from './module-property-option-item.model';
import { SpecProperty, UIProperty } from './module-specification.model';

export class SpecificationPropertyObject {
    get specType(): ModuleDataType {
        return this.getDataType() as ModuleDataType;
    }
    get uiControlType(): UiControlType {
        return this.getUiControlType() as UiControlType;
    }

    /**
     * Retrieves the type part of the spec-property (i.e. spec['type:textbox'] object)
     */
    get widgetSpecificSpecProperty(): ModulePropertySpecification {
        return this.specProperty[moduleDataTypePrefix + this.specType];
    }

    /**
     * Retrieves the type part of the ui-property (i.e. ui['widget:slider'] object)
     */
    get widgetSpecificUiProperty(): ModuleWidget | undefined {
        if (this.uiProperty != null) {
            return this.uiProperty[moduleWidgetTypePrefix + this.uiControlType];
        }
    }

    constructor(
        public key: string,
        public type: InputOutputType,
        public specProperty: SpecProperty<unknown>,
        public uiProperty?: UIProperty,
    ) {}

    changeSpecTypeTo(newDataType: ModuleDataType): void {
        const currentDataTypeKey: string = moduleDataTypePrefix + this.getDataType();

        // delete old dataType part from spec property
        delete this.specProperty[currentDataTypeKey];

        const newDataTypeKey: string = moduleDataTypePrefix + newDataType;
        const NewType = this.getPropertySpecificationConstructor(newDataType);
        this.specProperty[newDataTypeKey] = typeof NewType === 'function' ? new NewType() : NewType;
    }

    changeUiControlTypeTo(newUiControlType: UiControlType) {
        // Validate: Is UI control type valid for current specType?
        if (this.isUiControlTypeValidForCurrentSpecType(newUiControlType) && this.uiProperty != null) {
            const currentUiControlTypeKey: string = moduleWidgetTypePrefix + this.getUiControlType();

            // delete current object from UI part
            delete this.uiProperty[currentUiControlTypeKey];

            const widgetNew: ModuleWidgetConstructor | undefined = this.getPropertyWidgetConstructor(newUiControlType);

            const newUiTypeKey: string = moduleWidgetTypePrefix + newUiControlType;
            this.uiProperty[newUiTypeKey] = widgetNew ? new widgetNew() : null;

            // After changing the UI type we must validate if the default value is still valid
            if (this.isValueValidForUiControlType(this.specProperty.default, this.uiControlType) === false) {
                this.specProperty.default = undefined;
            }
        } else {
            console.error(newUiControlType + ' is not valid for ' + this.specType);
        }
    }

    private getUiControlType(): UiControlType | undefined {
        let controlType: UiControlType | undefined = undefined;

        if (this.uiProperty != null) {
            const typeKey: string | undefined = Object.keys(this.uiProperty).find((key: string) => key.startsWith(moduleWidgetTypePrefix));

            if (typeKey != null) {
                controlType = typeKey.replace(moduleWidgetTypePrefix, '') as UiControlType;
            }
        }
        return controlType;
    }

    /**
     * Determines if the default value is still valid for the given UiControlType
     */
    private isValueValidForUiControlType(val: unknown, control: UiControlType, items?: StringResource[]): boolean {
        let isValid: boolean = true;

        if (val != null) {
            if (control === 'dropdown' || control === 'radiobuttongroup') {
                // If no items are defined, the value cannot be valid.
                if (items == null || (items != null && items.length === 0)) {
                    isValid = false;
                } else {
                    // If items are defined, check if the value can be found among them.
                    isValid = items.find((item: StringResource) => item.label === val) != null;
                }
            }
        }

        return isValid;
    }

    private isUiControlTypeValidForCurrentSpecType(controlType: UiControlType): boolean {
        const isValid =
            moduleDataTypes.find((module: ModulePropertyOptionItem) => {
                return module.value === this.specType && module.widgets.includes(controlType);
            }) != null;

        return isValid;
    }

    private getDataType(): ModuleDataType | undefined {
        let type: ModuleDataType | undefined;
        const typeKey: string | undefined = Object.keys(this.specProperty).find((key: string) => key.startsWith(moduleDataTypePrefix));
        if (typeKey != null) {
            type = typeKey.replace(moduleDataTypePrefix, '') as ModuleDataType;
        }
        return type;
    }

    private getPropertyWidgetConstructor(moduleControlType: UiControlType): ModuleWidgetConstructor | undefined {
        let widgetConstructor: ModuleWidgetConstructor | undefined = undefined;

        switch (moduleControlType) {
            case 'textbox':
                widgetConstructor = TextBoxWidget;
                break;
            case 'slider':
                widgetConstructor = SliderWidget;
                break;
            case 'slider_range':
                widgetConstructor = RangeSliderWidget;
                break;
            case 'checkbox':
                widgetConstructor = CheckboxWidget;
                break;
            case 'radiobuttongroup':
                widgetConstructor = RadioButtonGroupWidget;
                break;
            case 'dropdown':
                widgetConstructor = DropdownWidget;
                break;
            case 'file_dialog':
                widgetConstructor = FileDialogWidget;
                break;
            case 'none':
                widgetConstructor = undefined;
                break;
        }

        return widgetConstructor;
    }

    private getPropertySpecificationConstructor(moduleDataType: ModuleDataType): ModulePropertyConstructor {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let propConstructor: ModulePropertyConstructor = <any>[];

        switch (moduleDataType) {
            case 'integer':
                propConstructor = IntegerSpecificationModel;
                break;
            case 'number':
                propConstructor = NumberSpecificationModel;
                break;
            case 'range':
                propConstructor = RangeSpecificationModel;
                break;
            case 'string':
                propConstructor = StringSpecificationModel;
                break;
            case 'file':
                propConstructor = FileSpecificationModel;
                break;
            case 'choice_binary':
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                propConstructor = <any>null;
                break;
            case 'choice_single':
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                propConstructor = <any>[];
                break;
            case 'list[file]':
                propConstructor = FileListSpecificationModel;
                break;
        }

        return propConstructor;
    }
}
